import React from 'react';
import { Box, Checkbox, FormControlLabel, Grid, InputLabel, OutlinedInput, Paper } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import BrazilianNumberInput from '../../../components/brazilian-number-input';
import PageHeader from '../../../components/page-header';
import { DateField, FieldError, FormPageFooter, FormSectionTitle } from '../../../components/form-page-parts';
import { LookupSelect } from '../shared/lookup-select';
import { SupplierFields } from '../shared/supplier-fields';
import { useAmendmentCreateEditState } from './context/amendment-create-edit-context';
import { useAmendmentCreateEditController } from './context/amendment-create-edit-controller';
import { validateAmendment } from './context/contract-validation';
import { IAmendmentAddEditRequest } from '../../../model/scp/contract/amendment/request/amendment-add-edit-request';

const AMENDMENT_TYPE_OPTIONS = [
    { value: 1, label: 'Termo de Rescisão' },
    { value: 2, label: 'Termo de Aditivo' },
    { value: 3, label: 'Termo de Apostilamento' },
];

const QUALIFICATIONS: { name: string; field: keyof IAmendmentAddEditRequest; label: string }[] = [
    { name: 'check-qualification-addition-suppression', field: 'qualificationAdditionSuppression', label: 'Acréscimo/Supressão' },
    { name: 'check-qualification-validity', field: 'qualificationValidity', label: 'Vigência' },
    { name: 'check-qualification-supplier', field: 'qualificationSupplier', label: 'Fornecedor' },
    { name: 'check-qualification-informative', field: 'qualificationInformative', label: 'Informativo' },
    { name: 'check-qualification-readjustment', field: 'qualificationReadjustment', label: 'Reajuste' },
];

const MONEY_FIELDS: { name: string; field: keyof IAmendmentAddEditRequest; label: string }[] = [
    { name: 'input-initial-value', field: 'initialValue', label: 'Valor Inicial' },
    { name: 'input-increase-value', field: 'increaseValue', label: 'Valor Acrescido' },
    { name: 'input-decrease-value', field: 'decreaseValue', label: 'Valor Reduzido' },
    { name: 'input-global-value', field: 'globalValue', label: 'Valor Global' },
    { name: 'input-installment-value', field: 'installmentValue', label: 'Valor Parcela' },
];

export function AmendmentCreateEditComponent() {
    const ctrl = useAmendmentCreateEditController();
    const { amendmentRequest: request, showValidationErrors } = useAmendmentCreateEditState();
    const errors = showValidationErrors ? validateAmendment(request, ctrl.isEdit) : {};

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title={ctrl.isEdit ? 'Editar Aditivo' : 'Novo Aditivo'}
                    subtitle={ctrl.isEdit ? 'Atualize as informações do termo contratual' : 'Cadastre um novo termo para o contrato'}
                    breadcrumbs={[
                        { label: 'Contratos', href: '/contratos' },
                        { label: 'Aditivos', href: ctrl.listRoute },
                        { label: ctrl.isEdit ? 'Editar' : 'Novo' },
                    ]}
                />
            </Grid>

            <Grid item xs={12}>
                <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }} elevation={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormSectionTitle title="Identificação do Termo" first />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <LookupSelect required label="Tipo de Aditivo" name="select-amendment-type" value={request.amendmentTypeId} options={AMENDMENT_TYPE_OPTIONS} onChange={ctrl.handleOnChange} error={errors.amendmentTypeId} emptyLabel="Selecionar" />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <InputLabel required>Número do Termo</InputLabel>
                                <OutlinedInput fullWidth autoComplete="off" name="input-amendment-number" value={request.amendmentNumber || ''} onChange={ctrl.handleOnChange} error={!!errors.amendmentNumber} />
                                <FieldError message={errors.amendmentNumber} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DateField required label="Data de Assinatura" value={request.signDate} onChange={(value) => ctrl.handleDateChange('signDate', value)} error={errors.signDate} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DateField label="Data de Publicação" value={request.publicationDate} onChange={(value) => ctrl.handleDateChange('publicationDate', value)} />
                            </Grid>

                            <Grid item xs={12}>
                                <FormSectionTitle title="Período de Vigência" />
                            </Grid>
                            <Grid item xs={12} sm={4} md={3}>
                                <DateField label="Início de Vigência" value={request.effectiveDateStart} onChange={(value) => ctrl.handleDateChange('effectiveDateStart', value)} />
                            </Grid>
                            <Grid item xs={12} sm={4} md={3}>
                                <DateField label="Fim de Vigência" value={request.effectiveDateEnd} onChange={(value) => ctrl.handleDateChange('effectiveDateEnd', value)} />
                            </Grid>
                            <Grid item xs={12} sm={4} md={3}>
                                <InputLabel>Prazo Aditado (Dias)</InputLabel>
                                <OutlinedInput fullWidth autoComplete="off" type="number" inputProps={{ min: 0 }} name="input-additional-deadline-days" value={request.additionalDeadlineDays ?? ''} onChange={ctrl.handleOnChange} />
                            </Grid>

                            <Grid item xs={12}>
                                <FormSectionTitle title="Qualificações do Termo" />
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                    {QUALIFICATIONS.map(({ name, field, label }) => (
                                        <FormControlLabel key={name} label={label} control={<Checkbox name={name} checked={!!request[field]} onChange={ctrl.handleOnChange} />} />
                                    ))}
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                <FormSectionTitle title="Dados do Fornecedor" />
                            </Grid>
                            <SupplierFields
                                required
                                names={{ type: 'select-supplier-type', document: 'input-supplier-number-identification', name: 'input-supplier-name' }}
                                type={request.supplierType}
                                document={request.supplierNumberIdentification}
                                name={request.supplierName}
                                onChange={ctrl.handleOnChange}
                                errors={{ type: errors.supplierType, document: errors.supplierNumberIdentification, name: errors.supplierName }}
                            />

                            <Grid item xs={12}>
                                <FormSectionTitle title="Valores Financeiros" />
                            </Grid>
                            {MONEY_FIELDS.map(({ name, field, label }) => (
                                <Grid item xs={12} sm={6} md={2} key={name}>
                                    <InputLabel>{label}</InputLabel>
                                    <BrazilianNumberInput fullWidth value={(request[field] as number) || 0} onChange={(value) => ctrl.handleOnChange({ target: { name, value } })} decimals={2} prefix="R$" placeholder="0,00" />
                                </Grid>
                            ))}
                            <Grid item xs={12} sm={6} md={2}>
                                <InputLabel>Nº Parcelas</InputLabel>
                                <OutlinedInput fullWidth autoComplete="off" type="number" inputProps={{ min: 0, max: 1000 }} name="input-number-installments" value={request.numberInstallments ?? ''} onChange={ctrl.handleOnChange} />
                            </Grid>

                            <Grid item xs={12}>
                                <FormSectionTitle title="Descrições e Observações" />
                            </Grid>
                            <Grid item xs={12}>
                                <InputLabel required>Objeto do Termo</InputLabel>
                                <OutlinedInput fullWidth autoComplete="off" multiline rows={4} name="input-amendment-object" value={request.amendmentObject || ''} onChange={ctrl.handleOnChange} error={!!errors.amendmentObject} />
                                <FieldError message={errors.amendmentObject} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputLabel>Fundamento Legal</InputLabel>
                                <OutlinedInput fullWidth autoComplete="off" multiline rows={3} name="input-legal-basis" value={request.legalBasis || ''} onChange={ctrl.handleOnChange} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputLabel>Informação Complementar</InputLabel>
                                <OutlinedInput fullWidth autoComplete="off" multiline rows={3} name="input-additional-information" value={request.additionalInformation || ''} onChange={ctrl.handleOnChange} />
                            </Grid>
                            <Grid item xs={12}>
                                <InputLabel>Observação Informativa</InputLabel>
                                <OutlinedInput fullWidth autoComplete="off" multiline rows={3} name="input-informative-observation" value={request.informativeObservation || ''} onChange={ctrl.handleOnChange} />
                            </Grid>
                            {ctrl.isEdit && (
                                <Grid item xs={12}>
                                    <InputLabel required>Justificativa da Alteração</InputLabel>
                                    <OutlinedInput fullWidth autoComplete="off" multiline rows={3} name="input-justification" value={request.justification || ''} onChange={ctrl.handleOnChange} error={!!errors.justification} />
                                    <FieldError message={errors.justification} />
                                </Grid>
                            )}
                        </Grid>

                        <FormPageFooter onCancel={ctrl.navigateToList} onSave={ctrl.handleSave} isSaving={ctrl.isSaving} />
                    </LocalizationProvider>
                </Paper>
            </Grid>
        </React.Fragment>
    );
}
